import React from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { compactActionSx, money } from "./ownerCenterUi";

function dateTimeInputNow() {
  const date = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function newRequestKey() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `pay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function dueLabel(value) {
  if (!value) return "sin fecha";
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value));
}

export default function OwnerRecurringRecordDialog({ open, onClose, plan, occurrence = null, accounts = [], onSaved }) {
  const [resultOccurrence, setResultOccurrence] = React.useState(null);
  const target = resultOccurrence || occurrence || plan?.nextOccurrence || null;
  const [amount, setAmount] = React.useState("");
  const [expectedAmount, setExpectedAmount] = React.useState("");
  const [accountId, setAccountId] = React.useState("");
  const [occurredAt, setOccurredAt] = React.useState(dateTimeInputNow());
  const [requestKey, setRequestKey] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const eligibleAccounts = accounts.filter((account) => !account.archived && account.currency === plan?.currency);
  React.useEffect(() => {
    if (!open || !plan || !target) return;
    setResultOccurrence(null);
    const initialTarget = occurrence || plan?.nextOccurrence || null;
    setAmount(String(Number(initialTarget?.pendingAmount || 0) || ""));
    setExpectedAmount(String(Number(initialTarget?.expectedAmount || plan.projectedAmount || 0) || ""));
    setAccountId(plan.defaultAccountId || eligibleAccounts[0]?.id || eligibleAccounts[0]?._id || "");
    setOccurredAt(dateTimeInputNow());
    setRequestKey(newRequestKey());
    setError("");
    setSuccess("");
  }, [open, plan, occurrence?.periodKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const amountNumber = Number(amount || 0);
  const expectedNumber = Number(expectedAmount || 0);
  const alreadyPaid = Number(target?.paidAmount || 0);
  const effectivePending = plan?.amountMode === "variable" && expectedNumber > 0
    ? Math.max(0, expectedNumber - alreadyPaid)
    : Number(target?.pendingAmount || 0);
  const remainingAfter = Math.max(0, effectivePending - amountNumber);

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const response = await ownerApi.recordRecurringFinance(plan.id || plan._id, {
        periodKey: target.periodKey,
        requestKey,
        amount: amountNumber,
        ...(plan.amountMode === "variable" ? { expectedAmount: expectedNumber } : {}),
        accountId,
        occurredAt,
      });
      const updated = response?.occurrence || null;
      setResultOccurrence(updated);
      const remaining = Number(updated?.pendingAmount ?? response?.remainingAmount ?? 0);
      const nextOccurrence = response?.nextOccurrence || null;
      setSuccess(remaining > .005
        ? `${plan?.direction === "income" ? "Cobro" : "Pago"} registrado. Quedan ${money(remaining, plan?.currency)} de este período.`
        : nextOccurrence
          ? `Período pagado. Próximo vencimiento: ${dueLabel(nextOccurrence.dueAt)}.`
          : `Período pagado. No quedan vencimientos abiertos en este acuerdo.`);
      if (remaining > .005) {
        setAmount(String(remaining));
        setExpectedAmount(String(Number(updated?.expectedAmount || expectedNumber || 0) || ""));
        setRequestKey(newRequestKey());
        setOccurredAt(dateTimeInputNow());
      }
      onSaved?.(response);
    } catch (err) {
      setError(formatApiError(err, "No pude registrar este pago recurrente. Podés reintentar sin duplicar el movimiento."));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography component="div" sx={{ fontWeight: 1000, fontSize: "1.1rem" }}>{plan?.direction === "income" ? "Registrar cobro recurrente" : "Registrar pago recurrente"}</Typography>
        <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".82rem", mt: .3 }}>{plan?.name} · {target?.periodKey || "período"} · vence {dueLabel(target?.dueAt)}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.35}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, p: 1.2, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.035)" }}>
            <Box><Typography sx={{ color: "text.secondary", fontSize: ".7rem", fontWeight: 800 }}>Esperado</Typography><Typography sx={{ fontWeight: 950 }}>{money(plan?.amountMode === "variable" ? expectedNumber : target?.expectedAmount, plan?.currency)}</Typography></Box>
            <Box><Typography sx={{ color: "text.secondary", fontSize: ".7rem", fontWeight: 800 }}>Ya {plan?.direction === "income" ? "cobrado" : "pagado"}</Typography><Typography sx={{ fontWeight: 950 }}>{money(alreadyPaid, plan?.currency)}</Typography></Box>
            <Box><Typography sx={{ color: "text.secondary", fontSize: ".7rem", fontWeight: 800 }}>Pendiente</Typography><Typography sx={{ fontWeight: 1000 }}>{money(effectivePending, plan?.currency)}</Typography></Box>
          </Box>
          {plan?.amountMode === "variable" ? <TextField fullWidth type="number" label={`Esperado para este período · ${plan?.currency || ""}`} value={expectedAmount} onChange={(event) => setExpectedAmount(event.target.value)} helperText="Ajusta sólo la expectativa de este período. No mueve dinero ni cambia otros vencimientos." /> : null}
          <TextField autoFocus fullWidth type="number" label={`Registrar ahora · ${plan?.currency || ""}`} value={amount} onChange={(event) => setAmount(event.target.value)} helperText={remainingAfter > .005 ? `Después quedarán ${money(remainingAfter, plan?.currency)} pendientes.` : "Este período quedará pagado."} />
          <FormControl fullWidth><InputLabel>Cuenta</InputLabel><Select label="Cuenta" value={accountId} onChange={(event) => setAccountId(event.target.value)}>{eligibleAccounts.map((account) => <MenuItem key={account.id || account._id} value={account.id || account._id}>{account.name} · {account.currency}</MenuItem>)}</Select></FormControl>
          <TextField fullWidth type="datetime-local" label="Cuándo ocurrió" InputLabelProps={{ shrink: true }} value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.6 }}>
        <Button onClick={onClose} disabled={saving} sx={compactActionSx}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving || effectivePending <= .005 || !(amountNumber > 0) || amountNumber > effectivePending + .005 || !accountId || !target?.periodKey || (plan?.amountMode === "variable" && !(expectedNumber > 0))} sx={{ ...compactActionSx, px: 2.2 }}>{saving ? "Registrando…" : Number(target?.paidAmount || 0) > .005 ? `Registrar otro ${plan?.direction === "income" ? "cobro" : "pago"}` : plan?.direction === "income" ? "Registrar cobro" : "Registrar pago"}</Button>
      </DialogActions>
    </Dialog>
  );
}
